
import Vue from 'vue';
import ViewService from '@/services/ViewService';
import { extractErrorMessage } from '@/utils/Util';

import SimpleFiltersCard from '@/components/filters/SimpleFiltersCard.vue';
import ReadingGroupAggregationForm from '@/components/entities/view/ReadingGroupAggregationForm.vue';

type EntityType = 'properties' | 'connectors' | 'devices' | 'readings';
type RequestType =
  | 'property-collection'
  | 'connector-collection'
  | 'device-collection'
  | 'reading-collection'
  | 'reading-aggregates-collection';

type AlertType = 'success' | 'info' | 'warning' | 'error';

export default Vue.extend({
  name: 'ViewCreateDialog',
  components: { SimpleFiltersCard, ReadingGroupAggregationForm },

  data() {
    return {
      localdialog: false,
      loading: false,
      valid: false,

      alertVisible: false,
      alertType: 'error' as AlertType,
      alertText: '',

      ENTITY_TYPES: [
        'properties',
        'connectors',
        'devices',
        'readings',
      ] as EntityType[],

      REQUEST_TYPES_BY_ENTITY: {
        properties: ['property-collection'],
        connectors: ['connector-collection'],
        devices: ['device-collection'],
        readings: ['reading-collection', 'reading-aggregates-collection'],
      } as Record<EntityType, RequestType[]>,

      form: {
        description: '',
        entityType: '' as '' | EntityType,
        requestType: '' as '' | RequestType,
      },

      requestConfigUi: {} as any,
    };
  },

  computed: {
    requiredRule(): any[] {
      const msg = String((this as any).$t('required'));
      return [(value: any) => !!String(value ?? '').trim() || msg];
    },

    currentTenant(): string {
      return (this as any).$store.state.session.selectedTenant.uuid;
    },

    requestTypesForEntity(): string[] {
      if (!this.form.entityType) return [];
      return (this.REQUEST_TYPES_BY_ENTITY as any)[this.form.entityType] || [];
    },

    isReadingAggregates(): boolean {
      return (
        this.form.entityType === 'readings' &&
        this.form.requestType === 'reading-aggregates-collection'
      );
    },

    viewFilterConfig(): any[] {
      const selectedEntityType = this.form.entityType as EntityType;

      if (selectedEntityType === 'properties') {
        return [
          { attribute: 'name', always: true },
          { attribute: 'aksId', always: true },
          { attribute: 'identifier', always: true },
          { attribute: 'street', always: true },
          { attribute: 'postalCode' },
          { attribute: 'city' },
          { attribute: 'country' },
          { attribute: 'tagIds' },
          { attribute: 'createdAt' },
        ];
      }

      if (selectedEntityType === 'devices') {
        return [
          { attribute: 'aksId', always: true },
          { attribute: 'localAksId', always: true },
          { attribute: 'description', always: true },
          { attribute: 'type', always: true },
          { attribute: 'connectorId', always: true },
          { attribute: 'propertyId', always: true },
          { attribute: 'tagIds' },
          { attribute: 'unit' },
          { attribute: 'alerting' },
          { attribute: 'isRelative' },
          { attribute: 'createdAt' },
        ];
      }

      if (selectedEntityType === 'connectors') {
        return [
          { attribute: 'name', always: true },
          { attribute: 'tagIds', always: true },
          { attribute: 'createdAt' },
        ];
      }

      if (selectedEntityType === 'readings') {
        return [
          { attribute: 'deviceId', always: true },
          { attribute: 'connectorId', always: true },
          { attribute: 'propertyId', always: true },
          { attribute: 'flag' },
          { attribute: 'createdAt' },
        ];
      }

      return [];
    },

    canSubmit(): boolean {
      const hasDescription = !!String(this.form.description).trim();
      const hasEntityType = !!String(this.form.entityType).trim();
      const hasRequestType = !!String(this.form.requestType).trim();

      return (
        !this.loading &&
        this.valid &&
        hasDescription &&
        hasEntityType &&
        hasRequestType
      );
    },
  },

  watch: {
    localdialog(isOpen: boolean) {
      if (!isOpen) this.reset();
    },

    'form.entityType'() {
      this.form.requestType = '' as any;
      this.requestConfigUi = {};
      this.$nextTick(() => this.validateSilently());
    },

    'form.requestType'() {
      this.requestConfigUi = {};
      this.$nextTick(() => this.validateSilently());
    },
  },

  methods: {
    validateSilently() {
      const formRef = this.$refs.createForm as any;
      if (formRef?.validate) formRef.validate();
    },

    resetAlertOnly() {
      this.alertVisible = false;
      this.alertText = '';
      this.alertType = 'error';
    },

    reset() {
      this.form.description = '';
      this.form.entityType = '' as any;
      this.form.requestType = '' as any;
      this.requestConfigUi = {};
      this.resetAlertOnly();
      this.loading = false;

      this.$nextTick(() => {
        const formRef = this.$refs.createForm as any;
        if (formRef?.resetValidation) formRef.resetValidation();
      });
    },

    dismiss() {
      if (this.loading) return;
      this.reset();
      this.localdialog = false;
    },

    validate() {
      const formRef = this.$refs.createForm as any;
      const isValid = formRef?.validate ? formRef.validate() : false;
      if (isValid) this.createView();
    },

    async createView() {
      this.resetAlertOnly();
      this.loading = true;

      try {
        const requestConfig: any = { ...(this.requestConfigUi || {}) };
        Object.keys(requestConfig).forEach((k) => {
          const v = requestConfig[k];
          if (v === null || v === undefined) delete requestConfig[k];
          else if (typeof v === 'string' && v.trim() === '') delete requestConfig[k];
          else if (Array.isArray(v) && v.length === 0) delete requestConfig[k];
        });

        const createdView = await ViewService.create(
          this.currentTenant,
          String(this.form.description).trim(),
          String(this.form.entityType).trim(),
          String(this.form.requestType).trim(),
          requestConfig
        );

        this.alertType = 'success';
        this.alertText = String((this as any).$t('view.successCreate'));
        this.alertVisible = true;

        this.$emit('success', createdView);

        setTimeout(() => this.dismiss(), 1500);
      } catch (error: any) {
        this.alertType = 'error';
        const base = String((this as any).$t('view.errorCreate'));
        this.alertText = base + ' ' + extractErrorMessage(error);
        this.alertVisible = true;
      } finally {
        this.loading = false;
      }
    },
  },
});
